import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterService } from '@progress/kendo-angular-grid';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import {
  IonIcon,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  addOutline,
  closeOutline,
  funnelOutline,
  refreshOutline,
  layersOutline,
} from 'ionicons/icons';

type FilterLogic = 'and' | 'or';

type OperatorOption = {
  value: string;
  text: string;
  needsValue: boolean;
};

type ConditionDraft = {
  id: string;
  operator: string;
  value: any;
};

@Component({
  selector: 'lib-kendo-multi-filter-menu',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonIcon,
  ],
  templateUrl: './kendo-multi-filter-menu.component.html',
  styleUrl: './kendo-multi-filter-menu.component.css',
})
export class KendoMultiFilterMenuComponent implements OnInit, OnChanges {
  @Input({ required: true }) column!: any;
  @Input() filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined;
  @Input({ required: true }) filterService!: FilterService;

  @Output() applyFilter = new EventEmitter<CompositeFilterDescriptor>();
  @Output() clearFilter = new EventEmitter<void>();

  logic: FilterLogic = 'and';
  conditions: ConditionDraft[] = [];

  operatorOptionsList: OperatorOption[] = [];

  constructor() {
    addIcons({
      addOutline,
      closeOutline,
      funnelOutline,
      refreshOutline,
      layersOutline,
    });
  }

  ngOnInit(): void {
    if (this.conditions.length === 0) {
      this.operatorOptionsList = this.buildOperatorOptions();
      this.resetFromInputs();
    }
  }

  ngOnChanges(): void {
    this.operatorOptionsList = this.buildOperatorOptions();
    this.resetFromInputs();
  }

  addCondition(): void {
    this.conditions = [
      ...this.conditions,
      { id: this.newId(), operator: this.defaultOperator(), value: this.defaultValue() },
    ];
  }

  removeCondition(ix: number): void {
    if (this.conditions.length <= 1) {
      this.conditions = [{ id: this.newId(), operator: this.defaultOperator(), value: this.defaultValue() }];
      return;
    }
    this.conditions = this.conditions.filter((_, i) => i !== ix);
  }

  apply(): void {
    const field = this.field();
    if (!field) {
      this.clearColumn();
      return;
    }

    const leafs = this.buildLeafFilters(field);
    const next: CompositeFilterDescriptor = { logic: this.logic, filters: leafs };
    try {
      this.filterService?.filter?.(next);
    } catch {}
    this.applyFilter.emit(next);
  }

  clearColumn(): void {
    const next: CompositeFilterDescriptor = { logic: 'and', filters: [] };
    try {
      this.filterService?.filter?.(next);
    } catch {}
    this.clearFilter.emit();
  }

  private buildOperatorOptions(): OperatorOption[] {
    const t = String(this.column?.filter ?? 'text').toLowerCase();
    if (t === 'numeric' || t === 'number') {
      return [
        { value: 'eq', text: 'Equals', needsValue: true },
        { value: 'neq', text: 'Not equal', needsValue: true },
        { value: 'gt', text: 'Greater than', needsValue: true },
        { value: 'gte', text: 'Greater or equal', needsValue: true },
        { value: 'lt', text: 'Less than', needsValue: true },
        { value: 'lte', text: 'Less or equal', needsValue: true },
        { value: 'isnull', text: 'Is null', needsValue: false },
        { value: 'isnotnull', text: 'Is not null', needsValue: false },
      ];
    }
    if (t === 'date' || t === 'datetime') {
      return [
        { value: 'eq', text: 'Equals', needsValue: true },
        { value: 'neq', text: 'Not equal', needsValue: true },
        { value: 'gt', text: 'After', needsValue: true },
        { value: 'gte', text: 'After or equal', needsValue: true },
        { value: 'lt', text: 'Before', needsValue: true },
        { value: 'lte', text: 'Before or equal', needsValue: true },
        { value: 'isnull', text: 'Is null', needsValue: false },
        { value: 'isnotnull', text: 'Is not null', needsValue: false },
      ];
    }
    if (t === 'boolean' || t === 'bool') {
      return [
        { value: 'eq', text: 'Equals', needsValue: true },
        { value: 'neq', text: 'Not equal', needsValue: true },
        { value: 'isnull', text: 'Is null', needsValue: false },
        { value: 'isnotnull', text: 'Is not null', needsValue: false },
      ];
    }
    // text (default)
    return [
      { value: 'contains', text: 'Contains', needsValue: true },
      { value: 'doesnotcontain', text: 'Does not contain', needsValue: true },
      { value: 'eq', text: 'Equals', needsValue: true },
      { value: 'neq', text: 'Not equal', needsValue: true },
      { value: 'startswith', text: 'Starts with', needsValue: true },
      { value: 'endswith', text: 'Ends with', needsValue: true },
      { value: 'isempty', text: 'Is empty', needsValue: false },
      { value: 'isnotempty', text: 'Is not empty', needsValue: false },
      { value: 'isnull', text: 'Is null', needsValue: false },
      { value: 'isnotnull', text: 'Is not null', needsValue: false },
    ];
  }

  needsValue(op: string): boolean {
    return this.operatorOptionsList.find((o) => o.value === op)?.needsValue ?? true;
  }

  inputKind(): 'text' | 'number' | 'datetime' | 'boolean' {
    const t = String(this.column?.filter ?? 'text').toLowerCase();
    if (t === 'numeric' || t === 'number') return 'number';
    if (t === 'date' || t === 'datetime') return 'datetime';
    if (t === 'boolean' || t === 'bool') return 'boolean';
    return 'text';
  }

  canApply(): boolean {
    return true;
  }

  private resetFromInputs(): void {
    const field = this.field();
    const comp = this.normalizeChildFilter(this.filter);

    this.logic = (comp.logic as any) ?? 'and';
    const leafs = this.flattenLeafFilters(comp).filter((x) => {
      if (!field) return true;
      return String(x.field ?? '') === field;
    });

    if (!leafs.length) {
      this.conditions = [{ id: this.newId(), operator: this.defaultOperator(), value: this.defaultValue() }];
      return;
    }

    this.conditions = leafs.map((x) => ({
      id: this.newId(),
      operator: String(x.operator ?? this.defaultOperator()),
      value: this.toDraftValue(x.value),
    }));
  }

  private toDraftValue(v: any): any {
    const kind = this.inputKind();
    if (kind === 'boolean') {
      if (typeof v === 'boolean') return v ? 'true' : 'false';
      if (v === 'true' || v === 'false') return v;
      return 'true';
    }
    if (kind === 'datetime') {
      if (v instanceof Date) return this.toDatetimeLocal(v);
      if (typeof v === 'string') return v;
      return '';
    }
    return v ?? '';
  }

  private buildLeafFilters(field: string): FilterDescriptor[] {
    const kind = this.inputKind();
    const options = this.operatorOptionsList;

    const leafs: FilterDescriptor[] = [];
    for (const c of this.conditions) {
      const op = String(c.operator || this.defaultOperator());
      const needs = options.find((o) => o.value === op)?.needsValue ?? true;
      if (!needs) {
        leafs.push({ field, operator: op as any, value: null });
        continue;
      }

      if (!this.hasMeaningfulValue(c.value)) continue;

      const value =
        kind === 'number' ? this.coerceNumber(c.value) :
        kind === 'boolean' ? this.coerceBoolean(c.value) :
        kind === 'datetime' ? this.coerceDate(c.value) :
        String(c.value);

      if (kind === 'number' && typeof value !== 'number') continue;
      if (kind === 'datetime' && !(value instanceof Date) && value !== null) continue;

      leafs.push({ field, operator: op as any, value });
    }
    return leafs;
  }

  private coerceNumber(v: any): number | null {
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  }

  private coerceBoolean(v: any): boolean {
    if (v === true || v === 'true') return true;
    return false;
  }

  private coerceDate(v: any): Date | null {
    if (v instanceof Date) return v;
    const s = String(v ?? '').trim();
    if (!s) return null;
    const d = new Date(s);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  private hasMeaningfulValue(v: any): boolean {
    if (v === 0) return true;
    if (v === false) return true;
    if (v === true) return true;
    const s = String(v ?? '').trim();
    return s.length > 0;
  }

  private defaultOperator(): string {
    const kind = this.inputKind();
    return kind === 'text' ? 'contains' : 'eq';
  }

  private defaultValue(): any {
    const kind = this.inputKind();
    if (kind === 'boolean') return 'true';
    return '';
  }

  private field(): string | null {
    if (typeof this.column === 'string') return this.column;
    const f = this.column?.field;
    return f != null ? String(f) : null;
  }

  private newId(): string {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  private toDatetimeLocal(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      d.getFullYear() +
      '-' +
      pad(d.getMonth() + 1) +
      '-' +
      pad(d.getDate()) +
      'T' +
      pad(d.getHours()) +
      ':' +
      pad(d.getMinutes())
    );
  }

  private normalizeChildFilter(
    filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined
  ): CompositeFilterDescriptor {
    if (!filter) return { logic: 'and', filters: [] };
    const comp = filter as CompositeFilterDescriptor;
    if (Array.isArray(comp.filters)) {
      return { logic: (comp.logic as any) ?? 'and', filters: [...(comp.filters ?? [])] };
    }
    return { logic: 'and', filters: [filter as any] };
  }

  private flattenLeafFilters(filter: CompositeFilterDescriptor): FilterDescriptor[] {
    return (filter.filters ?? []).flatMap((x) => {
      const comp = x as CompositeFilterDescriptor;
      if (Array.isArray(comp.filters)) {
        return this.flattenLeafFilters(comp);
      }
      return [x as FilterDescriptor];
    });
  }
}
